import { CheckCircle, Info, Lightbulb, Warning } from '@tempo-io/sui-icons';
import { useTranslation } from 'react-i18next';
import type { FindingGroup } from '@/features/agentic-insights/components/data/types';
import type { GroupToneTokens, TypeTokens } from '../tokens';
import { Field } from '../ui/Field';

/**
 * The classic tab's own group icons — the one place this surface reaches into `@tempo-io/sui-*`,
 * deliberately, so a group reads the same here as it does there. A distinct icon per group is also
 * what tells `opportunity` and `ontrack` apart, since both are green.
 */
const GROUP_ICON: Record<FindingGroup, typeof Warning> = {
  risk: Warning,
  opportunity: Lightbulb,
  ontrack: CheckCircle,
  dataReadiness: Info,
};

const GROUP_RULE: Record<FindingGroup, string> = {
  risk: 'bg-bg-warning',
  opportunity: 'bg-bg-success',
  ontrack: 'bg-bg-success',
  dataReadiness: 'bg-bg-info',
};

interface GroupHeadingProps {
  type: Pick<TypeTokens, 'metaValue'>;
  groupTone: GroupToneTokens;
  group: FindingGroup;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * A finding group's heading: the group's icon, its name in primary ink, its count, and a rule in the
 * group's own colour running to the end of the row.
 *
 * Deliberately stronger than the rest of this surface's labels. Everything else here is muted mono
 * on a grey hairline, and at that weight a group heading read as a caption on the row beneath it
 * rather than as a division of the run — the thing a reader uses to tell a risk from a note about
 * the data before reading either.
 *
 * The whole heading — icon, name, count and rule — is the section's toggle, so a reader working
 * through one group can put the others away. The count stays visible collapsed, which is what makes
 * a closed section still say something.
 */
export const GroupHeading = ({
  type,
  groupTone,
  group,
  count,
  collapsed,
  onToggle,
}: GroupHeadingProps) => {
  const { t } = useTranslation();
  const Icon = GROUP_ICON[group];

  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      data-testid={`agentic-x-section-${group}`}
      onClick={onToggle}
      // Nothing about a heading says "clickable" on its own, so the hover has to: the caret firms
      // up from a hint to a full-strength mark, and the name takes the underline every other
      // clickable word on this surface carries.
      className="group/section flex w-full items-center gap-2.5 text-left"
    >
      <Icon className={`size-4 shrink-0 ${groupTone[group]}`} aria-hidden="true" />
      <span className="font-mono text-xs font-semibold tracking-[0.14em] text-text-neutral-primary uppercase underline decoration-transparent decoration-1 underline-offset-4 transition-colors group-hover/section:decoration-border-neutral-bold">
        <Field name="group">{t(($) => $.loopAgenticInsights.group[group])}</Field>
      </span>
      <span className={`${type.metaValue} ${groupTone[group]}`}>{count}</span>
      {/* The caret is the only part that changes state, and it is drawn in the group's own tone so a
          collapsed section still reads as that group rather than as a generic closed row. Faint
          until the heading is hovered, so a page of sections is not a page of carets. */}
      <span
        className={`${type.metaValue} ${groupTone[group]} opacity-50 transition-opacity group-hover/section:opacity-100`}
        aria-hidden="true"
      >
        {collapsed ? '▸' : '▾'}
      </span>
      <span className={`h-px flex-1 opacity-40 ${GROUP_RULE[group]}`} aria-hidden="true" />
    </button>
  );
};
